import { useEffect, useMemo, useRef, useState } from 'react';
import {
  imagesToPdf,
  MAX_IMAGE_PAGES,
  suggestPdfNameFromImages,
  type ImagePdfPage,
} from '@/lib/pdf/imagesToPdf';

export interface PdfImagePageItem {
  id: string;
  blob: Blob;
  name: string;
  previewUrl: string;
}

interface PdfImagesToPdfModalProps {
  open: boolean;
  /** Seed pages when opening (upload / camera handoff) */
  seedPages?: PdfImagePageItem[] | null;
  seedKey?: number;
  /** Append captures from camera while this modal is already open */
  appendBatch?: PdfImagePageItem[] | null;
  appendKey?: number;
  onClose: () => void;
  onCreated: (payload: { bytes: ArrayBuffer; name: string }) => void;
  /** Receives current page count so camera knows remaining slots */
  onOpenCamera?: (currentPageCount: number) => void;
}

function makePage(blob: Blob, name: string): PdfImagePageItem {
  return {
    id: crypto.randomUUID(),
    blob,
    name,
    previewUrl: URL.createObjectURL(blob),
  };
}

export function filesToImagePages(files: FileList | File[]): PdfImagePageItem[] {
  const list = Array.from(files).filter((f) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic)$/i.test(f.name));
  return list.map((f) => makePage(f, f.name || 'Photo.jpg'));
}

export function blobToImagePage(blob: Blob, name = 'Capture.jpg'): PdfImagePageItem {
  return makePage(blob, name);
}

export function appendImagePages(
  current: PdfImagePageItem[],
  added: PdfImagePageItem[]
): PdfImagePageItem[] {
  const room = MAX_IMAGE_PAGES - current.length;
  if (room <= 0) {
    for (const p of added) URL.revokeObjectURL(p.previewUrl);
    return current;
  }
  const take = added.slice(0, room);
  for (const extra of added.slice(room)) URL.revokeObjectURL(extra.previewUrl);
  return [...current, ...take];
}

function revokeAll(list: PdfImagePageItem[]) {
  for (const p of list) URL.revokeObjectURL(p.previewUrl);
}

export default function PdfImagesToPdfModal({
  open,
  seedPages = null,
  seedKey = 0,
  appendBatch = null,
  appendKey = 0,
  onClose,
  onCreated,
  onOpenCamera,
}: PdfImagesToPdfModalProps) {
  const [pages, setPages] = useState<PdfImagePageItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const captureRef = useRef<HTMLInputElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpen.current = false;
      return;
    }
    // Fresh open or new seed batch from parent
    setBusy(false);
    setError(null);
    setPages(seedPages?.length ? seedPages : []);
    wasOpen.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seedPages applied when seedKey/open changes
  }, [open, seedKey]);

  useEffect(() => {
    if (!open || !appendBatch?.length || !wasOpen.current) return;
    setPages((prev) => {
      const next = appendImagePages(prev, appendBatch);
      if (next.length === prev.length) setError(`Maximum ${MAX_IMAGE_PAGES} pages`);
      else setError(null);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendKey]);

  const addFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const added = filesToImagePages(Array.from(files));
    if (!added.length) {
      setError('Choose image files (JPG, PNG, WebP…)');
      return;
    }
    setPages((prev) => {
      const next = appendImagePages(prev, added);
      if (next.length === prev.length) setError(`Maximum ${MAX_IMAGE_PAGES} pages`);
      else if (added.length > next.length - prev.length) {
        setError(`Only ${MAX_IMAGE_PAGES} pages allowed`);
      } else setError(null);
      return next;
    });
  };

  const removeAt = (id: string) => {
    setPages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const move = (id: string, dir: -1 | 1) => {
    setPages((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(next, 0, item);
      return copy;
    });
  };

  const handleClose = () => {
    if (busy) return;
    revokeAll(pages);
    setPages([]);
    onClose();
  };

  const handleCreate = async () => {
    if (!pages.length) {
      setError('Add at least one image');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload: ImagePdfPage[] = pages.map((p) => ({
        blob: p.blob,
        name: p.name,
      }));
      const bytes = await imagesToPdf(payload);
      const name = suggestPdfNameFromImages(payload, 'Scan.pdf');
      revokeAll(pages);
      setPages([]);
      onCreated({ bytes, name });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create PDF');
    } finally {
      setBusy(false);
    }
  };

  const pageCountLabel = useMemo(
    () => `${pages.length} page${pages.length === 1 ? '' : 's'}`,
    [pages.length]
  );

  if (!open) return null;

  return (
    <div className="pdfe-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="pdfe-modal pdfe-images-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdfe-images-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pdfe-modal-head">
          <h2 id="pdfe-images-title">Create PDF from images</h2>
          <button
            type="button"
            className="pdfe-modal-close"
            onClick={handleClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <div className="pdfe-images-body">
          <p className="pdfe-images-hint">
            Each image becomes one A4 page. Reorder, then create.
          </p>

          {pages.length === 0 ? (
            <div className="pdfe-images-empty">
              <p>No images yet</p>
              <div className="pdfe-images-empty-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => fileRef.current?.click()}
                >
                  Upload images
                </button>
                {onOpenCamera && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => onOpenCamera(pages.length)}
                  >
                    Scan with camera
                  </button>
                )}
              </div>
            </div>
          ) : (
            <ul className="pdfe-images-list">
              {pages.map((p, i) => (
                <li key={p.id} className="pdfe-images-card">
                  <img src={p.previewUrl} alt={`Page ${i + 1}`} />
                  <span className="pdfe-images-card-label">
                    {i + 1}. {p.name}
                  </span>
                  <div className="pdfe-images-card-actions">
                    <button
                      type="button"
                      className="pdfe-icon-btn"
                      title="Move up"
                      disabled={i === 0 || busy}
                      onClick={() => move(p.id, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="pdfe-icon-btn"
                      title="Move down"
                      disabled={i === pages.length - 1 || busy}
                      onClick={() => move(p.id, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="pdfe-icon-btn"
                      title="Remove"
                      disabled={busy}
                      onClick={() => removeAt(p.id)}
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="pdfe-sig-modal-error">{error}</p>}
        </div>

        <footer className="pdfe-modal-foot pdfe-images-foot">
          <div className="pdfe-images-foot-left">
            <span className="pdfe-images-count">{pageCountLabel}</span>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy || pages.length >= MAX_IMAGE_PAGES}
              onClick={() => fileRef.current?.click()}
            >
              Add images
            </button>
            {onOpenCamera && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || pages.length >= MAX_IMAGE_PAGES}
                onClick={() => onOpenCamera(pages.length)}
              >
                Camera
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || pages.length >= MAX_IMAGE_PAGES}
              onClick={() => captureRef.current?.click()}
              title="Use device camera (mobile)"
            >
              Take photo
            </button>
          </div>
          <div className="pdfe-images-foot-right">
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || pages.length === 0}
              onClick={() => void handleCreate()}
            >
              {busy ? 'Creating…' : 'Create PDF'}
            </button>
          </div>
        </footer>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const list = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = '';
            addFiles(list);
          }}
        />
        <input
          ref={captureRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const list = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = '';
            addFiles(list);
          }}
        />
      </div>
    </div>
  );
}
